import { useNavigate, useSearch } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";

import { carryFilters } from "@/lib/filters";

import { writeLastItem } from "../../filters";
import { ItemLink } from "../../links";
import { TabPage, useTabQuery } from "../../PageFrame";
import type { SourcingTab } from "../../routes";
import type { PageProps } from "../index";
import { dateGB, Footnote, MUTED_ROW, PageHead } from "../kit";
import { ShowMoreToggle, Tile } from "../margin_at_stake/Page";

export type WindowKey = "all" | "1" | "2" | "3" | "4";

/** C1 rev 1.9 `item-activity`: one object, never paginated. */
export interface ActivityTotals {
  items_n: number;
  added_n: number;
  live_n: number;
  dropped_n: number;
  scored_n: number;
  quoted_n: number;
  lines_n: number;
}

export interface ActivityRow {
  funnel: string;
  item_id: string;
  item_name: string;
  added_n: number;
  live_n: number;
  dropped_n: number;
  scored_n: number;
  quoted_n: number;
  lines_n: number;
  last_at: string | null;
  last_what: "Option added" | "Quote received" | "Option dropped" | null;
  in_window: boolean;
}

export interface ItemActivity {
  win: WindowKey;
  since: string | null;
  totals: ActivityTotals;
  activity: ActivityRow[];
}

export const WIN_KEY = "f_win";
export const WINDOWS: ReadonlyArray<readonly [WindowKey, string]> = [
  ["all", "All"],
  ["1", "1 week"],
  ["2", "2 weeks"],
  ["3", "3 weeks"],
  ["4", "4 weeks"],
];
export const PHASE_2 = "Coming in phase 2";
export const PHASE_2_TITLE = "Needs the enquiry log. Coming in phase 2.";
export const ALL_ITEMS = "← All items";
/** The per-item table shows this many rows until "Show more" (Ioan 2026-10-09). */
export const SUMMARY_TOP_N = 10;
export const COLUMNS = [
  "Item · last activity", "Options added", "Live / dropped", "Enquiries sent", "Suppliers replied", "Reply rate",
  "Quotes (suppliers · price lines)", "Quote rate", "Scored",
] as const;
/** The phase-2 figures: greyed tiles and greyed columns read this one list. */
export const PHASE_2_LABELS = ["Enquiries sent", "Suppliers replied", "Reply rate", "Quote rate"] as const;
const GREYED: ReadonlySet<string> = new Set<string>(PHASE_2_LABELS);

/** The summary's C1 call: a level of Item Overview, so not one of the registered tabs. */
export const ACTIVITY_TAB: SourcingTab = {
  tabKey: "item_overview",
  apiTab: "item-activity",
  label: "Item Overview",
  group: "HOME",
  needs: null,
  accepts: ["item", "funnel", "phase", "option", "geo", "dropped"],
  ignoresDropped: false,
};

/** The URL may hold 2 or "2" (the router parses numbers); anything else is all time. */
export function readWindow(search: Record<string, unknown>): WindowKey {
  const value = String(search[WIN_KEY] ?? "");
  return value === "1" || value === "2" || value === "3" || value === "4" ? value : "all";
}

export function windowSearch(previous: Record<string, unknown>, key: WindowKey): Record<string, unknown> {
  const next = { ...previous };
  if (key === "all") delete next[WIN_KEY];
  else next[WIN_KEY] = Number(key);
  delete next.page;
  return next;
}

export function windowLabel(key: WindowKey): string {
  return key === "all" ? "all time" : `the last ${key} week${key === "1" ? "" : "s"}`;
}

export function activityParams(key: WindowKey): Record<string, string> {
  return key === "all" ? {} : { win: key };
}

export function activityLine(row: ActivityRow, key: WindowKey): string {
  if (!row.in_window || row.last_at === null || row.last_what === null) return `No activity in ${windowLabel(key)}`;
  return `${row.last_what} · ${dateGB(row.last_at)}`;
}

/** Back to all items: the other filters and the window stay; the item, its option, line and variant go. */
export function allItemsSearch(previous: Record<string, unknown>): Record<string, unknown> {
  const next = carryFilters(previous);
  for (const key of ["f_item", "f_option", "f_line", "f_variant"]) delete next[key];
  return next;
}

export function WindowBar() {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const current = readWindow(search);
  return (
    <div role="group" aria-label="Date window" className="mb-3 mt-1 inline-flex self-start overflow-hidden rounded-full border border-ks-grey-2">
      {WINDOWS.map(([key, label]) => (
        <button
          key={key}
          type="button"
          data-nonmutating
          aria-pressed={current === key}
          className={`min-h-7 border-l border-ks-grey-2 px-3.5 py-1.5 text-xs first:border-l-0 ${
            current === key ? "bg-ks-purple font-bold text-white" : "bg-white text-ks-ink"
          }`}
          onClick={() =>
            void navigate({
              search: ((previous: Record<string, unknown>) => windowSearch(previous, key)) as never,
            } as never)
          }
        >
          {label}
        </button>
      ))}
    </div>
  );
}

export function PhaseTwoChip() {
  return (
    <span
      title={PHASE_2_TITLE}
      className="mt-1 inline-flex rounded-full border border-dashed border-ks-grey-2 bg-ks-grey-4 px-2 py-0.5 text-xs text-ks-grey-1"
    >
      {PHASE_2}
    </span>
  );
}

function PhaseTwoTile({ label }: { label: string }) {
  return (
    <div className="rounded-md border border-ks-grey-3 bg-ks-grey-4 px-3 py-2 text-xs text-ks-grey-2">
      {label}
      <span className="mt-0.5 block text-2xl font-semibold">—</span>
      <PhaseTwoChip />
    </div>
  );
}

/** What the window counts, under the Options added tile: the windows run up to today, each holding the shorter ones. */
export function addedSub(win: WindowKey): string {
  return win === "all" ? "since each item was created" : `in ${windowLabel(win)}, up to today`;
}

/** Live now ignores the date window: every option live today (the all-time call's live count), for the same filters. */
export function useLiveNow(): number | null {
  const all = useTabQuery<ItemActivity>(ACTIVITY_TAB, activityParams("all"));
  if (!all.isSuccess || all.data.meta.health.status === "failed") return null;
  return all.data.data?.totals.live_n ?? null;
}

/** `phaseTwo` false on the item page, where the enquiry figures come from the quote facts instead. */
export function ActivityTiles({ totals, phaseTwo = true, win = "all", liveNow = null }: {
  totals: ActivityTotals | null; phaseTwo?: boolean; win?: WindowKey; liveNow?: number | null;
}) {
  const show = (value: ReactNode) => (totals ? value : "—");
  return (
    <>
      <div className="grid grid-cols-2 gap-2.5 md:grid-cols-6">
        <Tile label="Live now" value={liveNow ?? "—"} sub="every live option today, any date" />
        <Tile label="Options added" value={show(totals?.added_n)} sub={addedSub(win)} />
        <Tile label="Live / dropped today" value={show(`${totals?.live_n} / ${totals?.dropped_n}`)} sub="of the options added, as they stand today" />
        <Tile label="Options scored" value={show(totals?.scored_n)} />
        <Tile label="Suppliers quoted" value={show(totals?.quoted_n)} />
        <Tile label="Price lines" value={show(totals?.lines_n)} />
      </div>
      {phaseTwo ? (
        <div className="mt-2.5 grid grid-cols-2 gap-2.5 md:grid-cols-4">
          {PHASE_2_LABELS.map((label) => (
            <PhaseTwoTile key={label} label={label} />
          ))}
        </div>
      ) : null}
    </>
  );
}

const LINE_A = "60,70.0 135,86.8 210,88.2 285,72.8 360,54.9 435,50.8 510,64.4 585,83.1 660,89.8 735,78.2 810,59.1 885,50.0";
const LINE_B = "60,122.0 135,116.5 210,105.0 285,98.1 360,102.2 435,113.4 510,121.5 585,119.0 660,108.3 735,99.1 810,99.9 885,110.1";

/** A greyed picture of the phase-2 chart: decorative lines, no data. */
export function RateChartPlaceholder() {
  return (
    <div className="mt-3 rounded-md border border-ks-grey-3 bg-ks-grey-4 p-3 text-ks-grey-2">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-bold">Reply rate and quote rate by week</h3>
        <PhaseTwoChip />
      </div>
      <svg viewBox="0 0 900 150" width="100%" height="150" role="img" aria-label="Reply rate and quote rate by week, coming in phase 2">
        {[20, 55, 90, 125].map((y) => (
          <line key={y} x1="40" x2="890" y1={y} y2={y} className="stroke-ks-grey-3" />
        ))}
        <polyline fill="none" strokeWidth="2" className="stroke-ks-grey-2" points={LINE_A} />
        <polyline fill="none" strokeWidth="2" strokeDasharray="5 4" className="stroke-ks-grey-2" points={LINE_B} />
      </svg>
      <Footnote>
        Reply rate = suppliers that replied ÷ enquiries sent. Quote rate = suppliers that sent a price ÷ enquiries sent.
        Needs every enquiry and reply recorded.
      </Footnote>
    </div>
  );
}

const TH = "border-b border-ks-grey-3 px-2 py-1.5 text-left font-bold";
const TD = "border-b border-ks-grey-4 px-2 py-1.5";
const NUM = "text-right tabular-nums";
const GREY = "bg-ks-grey-4 text-ks-grey-2";

function GreyCells() {
  return (
    <>
      {[0, 1, 2].map((i) => (
        <td key={i} className={`${TD} ${NUM} ${GREY}`}>—</td>
      ))}
    </>
  );
}

export function SummaryBody({ data, win, liveNow = null }: { data: ItemActivity; win: WindowKey; liveNow?: number | null }) {
  const t = data.totals;
  const [open, setOpen] = useState(false);
  const rows = open ? data.activity : data.activity.slice(0, SUMMARY_TOP_N);
  return (
    <>
      <ActivityTiles totals={t} win={win} liveNow={liveNow} />
      <RateChartPlaceholder />
      <h3 className="mb-2 mt-4 text-sm font-bold">
        Per item <span className="font-normal text-ks-grey-1">· latest activity first</span>
      </h3>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr>
              {COLUMNS.map((label, index) => (
                <th key={label} className={`${TH} ${index > 0 ? NUM : ""} ${GREYED.has(label) ? GREY : ""}`}>{label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.item_id} className={row.in_window ? "" : MUTED_ROW}>
                <td className={TD}>
                  <ItemLink itemId={row.item_id} from="item_overview">{row.item_name}</ItemLink>
                  <div className="text-xs text-ks-grey-1">{activityLine(row, win)}</div>
                </td>
                <td className={`${TD} ${NUM}`}>{row.added_n}</td>
                <td className={`${TD} ${NUM}`}>{row.live_n} / {row.dropped_n}</td>
                <GreyCells />
                <td className={`${TD} ${NUM}`}>{row.quoted_n} · {row.lines_n}</td>
                <td className={`${TD} ${NUM} ${GREY}`}>—</td>
                <td className={`${TD} ${NUM}`}>{row.scored_n}</td>
              </tr>
            ))}
            <tr className="font-bold">
              <td className={TD}>Total</td>
              <td className={`${TD} ${NUM}`}>{t.added_n}</td>
              <td className={`${TD} ${NUM}`}>{t.live_n} / {t.dropped_n}</td>
              <GreyCells />
              <td className={`${TD} ${NUM}`}>{t.quoted_n} · {t.lines_n}</td>
              <td className={`${TD} ${NUM} ${GREY}`}>—</td>
              <td className={`${TD} ${NUM}`}>{t.scored_n}</td>
            </tr>
          </tbody>
        </table>
      </div>
      {data.activity.length > SUMMARY_TOP_N ? <ShowMoreToggle open={open} onToggle={() => setOpen(!open)} /> : null}
      <Footnote>
        Greyed columns need every enquiry and reply recorded, coming in phase 2. With a date window, Options added, Scored and
        Quotes count what happened in the window; Live / dropped splits the options added in the window by where they are
        now. Prices exclude VAT.
      </Footnote>
    </>
  );
}

function LiveSummary({ data, win }: { data: ItemActivity; win: WindowKey }) {
  return <SummaryBody data={data} win={win} liveNow={useLiveNow()} />;
}

export function ItemSummaryPage({ tab, areaName }: PageProps) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const win = readWindow(search);
  return (
    <TabPage<ItemActivity>
      tab={ACTIVITY_TAB}
      areaName={areaName}
      extra={activityParams(win)}
      isEmpty={(data) => data.totals.items_n === 0}
      head={() => (
        <>
          <PageHead title={tab.label} sub={`Every sourcing item on one page · ${windowLabel(win)} · click an item for its quotes`} />
          <WindowBar />
        </>
      )}
    >
      {(data) => <LiveSummary data={data} win={win} />}
    </TabPage>
  );
}

/** The window tiles on one item's page: their own call, "—" until it answers and passes its check. */
export function ItemWindowTiles() {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const win = readWindow(search);
  const query = useTabQuery<ItemActivity>(ACTIVITY_TAB, activityParams(win));
  const data = query.isSuccess && query.data.meta.health.status !== "failed" ? query.data.data : null;
  const liveNow = useLiveNow();
  return (
    <div className="mb-4">
      <ActivityTiles totals={data?.totals ?? null} phaseTwo={false} win={win} liveNow={liveNow} />
    </div>
  );
}

export function AllItemsLink() {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      data-nonmutating
      className="mb-2 block w-fit self-start text-left text-sm text-ks-purple underline-offset-2 hover:underline focus:underline"
      onClick={() => {
        writeLastItem(null); // so the bar does not bring the item back on the next load
        void navigate({ search: ((previous: Record<string, unknown>) => allItemsSearch(previous)) as never } as never);
      }}
    >
      {ALL_ITEMS}
    </button>
  );
}
